import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { LeadPriority } from '../../../types/leads';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { TextArea } = Input;

const PriorityLeadList: React.FC = () => {
  const [priorityList, setPriorityList] = useState<LeadPriority[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPriority, setEditingPriority] = useState<LeadPriority | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadPriority.create');
  const canUpdate = hasPermission('leadPriority.update');
  const canDelete = hasPermission('leadPriority.delete');

  const fetchPriorities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-priority');
      setPriorityList(response.data.data || []);
    } catch (error: any) {
      message.error('Gagal mengambil data lead priority');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchPriorities();
  }, [fetchPriorities]);

  const showModal = (priority: LeadPriority | null = null) => {
    setEditingPriority(priority);
    form.setFieldsValue(
      priority
        ? {
            name: priority.name,
            description: priority.description,
          }
        : { name: '', description: '' }
    );
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingPriority(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingPriority) {
        await api.put(`lead-priority/${editingPriority.id}`, values);
        message.success('Prioritas Lead berhasil diperbarui');
      } else {
        await api.post('lead-priority', values);
        message.success('Prioritas Lead berhasil ditambahkan');
      }

      fetchPriorities();
      handleCancel();
    } catch (error) {
      message.error('Terjadi kesalahan. Silakan coba lagi.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: LeadPriority) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `/lead-priority/${record.id}`
        : `/lead-priority/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Prioritas Lead ${action} berhasil`);
      fetchPriorities();
    } catch (error) {
      message.error('Terjadi kesalahan. Silakan coba lagi.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Prioritas',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 120,
      render: (_: any, record: LeadPriority) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Apakah Anda yakin ingin ${
                record.is_active ? 'deactivate' : 'reactivate'
              } tipe ini?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Lead Priority Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Prioritas
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={priorityList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingPriority ? 'Edit Prioritas Lead' : 'Tambah Prioritas Lead Baru'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="leadPriorityForm" initialValues={{ is_default: false }}>
          <Form.Item
            name="name"
            label="Nama Prioritas"
            rules={[{ required: true, message: 'Nama prioritas tidak boleh kosong!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PriorityLeadList;